import {type ReactNode, useCallback, useId, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {format, formatDistanceToNow} from 'date-fns';
import {
  MoreHorizontal,
  RotateCw,
  RefreshCw,
  SendHorizontal,
  Trash2,
  NotebookPen,
  Scale,
  MessageSquare,
  Images,
  Film,
  ShieldAlert,
  MessageSquareQuote,
  TriangleAlert,
  Globe,
  Lock,
  type LucideIcon
} from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Spinner,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {type Submission, submissionsApi} from '@/api/submissions';
import {
  customerDecisionLabel,
  customerFeedbackDetails,
  isFalsePositiveDecision
} from '@/lib/customerFeedback';
import {useApolloRelayConfig} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {SubmissionStatusPill} from '@/components/submissions/SubmissionStatusPill';
import {FlagPicker} from '@/components/flags/Flag';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {SensitiveMediaButton} from '@/components/shared/SensitiveMedia';
import {ImageLightbox} from '@/components/shared/ImageLightbox';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {platformStatusTone, toneBadge, type StatusTone} from '@/lib/status';
import {cn} from '@/lib/utils';
import {type FlagColor} from '@/api/flags';
import {showToast} from '@/lib/toast';
import {type SubmissionsThumbnailSize, useUiStore} from '@/store/uiStore';

const THUMBNAIL_SIZE_CLASS: Record<SubmissionsThumbnailSize, string> = {
  sm: 'h-10 w-16',
  md: 'h-16 w-28',
  lg: 'h-24 w-40'
};

// Restrictions applied since the first check, shown beside the primary status so a still-available
// submission that has been age-gated or geo-blocked is visible without opening it.
// `short` is the text shown under a Restricted status, which has no platform message of its own -
// these changes are what made it restricted.
const PLATFORM_CHANGES: Record<string, {label: string; icon: LucideIcon; short?: string}> = {
  age_restricted: {label: 'Age restriction applied', icon: ShieldAlert, short: 'Age-gated'},
  content_warning: {label: 'Warning screen applied', icon: TriangleAlert, short: 'Warning screen'},
  region_restricted: {label: 'Region restriction added', icon: Globe, short: 'Region-blocked'},
  access_restricted: {label: 'Playback restricted', icon: Lock, short: 'Playback restricted'},
  // Reported by the item's own thumbnail history rather than a platform restriction - see SubmissionResponse.
  thumbnail_changed: {label: 'Thumbnail changed since submission', icon: Images}
};

// What the Status Check cell reads. QA review items carry these under the same names, without the
// customer's feedback, so the review queues show the same cell.
export type StatusCheckFields = Pick<
  Submission,
  | 'platform_status'
  | 'platform_feedback_message'
  | 'platform_reason_code'
  | 'platform_changes'
  | 'last_status_check_at'
> &
  Partial<Pick<Submission, 'customer_feedback'>>;

// The client's own outcome from an uploaded feedback file, as an icon beside the Status Check -
// both answer "is it still up?", one from our re-check and one from the customer. Verified means the
// client gave both a decision and a live status; partial means one was blank. Hover for the detail.
export function CustomerFeedbackIndicator({sub}: {readonly sub: StatusCheckFields}) {
  const feedback = sub.customer_feedback;
  if (!feedback) {
    return null;
  }

  // The decision is the customer's own, shown as they gave it. A labelled Badge with a leading icon,
  // like the app's other chips (ItemMeta's StatChip): a false positive (Approve / No violation) in the
  // soft destructive tone, any other decision in the tag tone, no decision in outline.
  const decision = customerDecisionLabel(feedback.last_decision);
  const variant = isFalsePositiveDecision(feedback.last_decision)
    ? 'destructive'
    : decision
      ? 'tag'
      : 'outline';
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge variant={variant} aria-label={`Customer feedback: ${decision ?? 'no decision'}`}>
            <MessageSquareQuote data-icon="inline-start" />
            {decision ?? 'Feedback'}
          </Badge>
        }
      />
      <TooltipContent side="bottom" align="start" className="max-w-xs text-left">
        <div className="space-y-1">
          <p className="font-medium">Customer feedback</p>
          <p>Decision: {decision ?? 'not given'}</p>
          {customerFeedbackDetails(feedback).map(({label, value}) => (
            <p key={label}>
              {label}: {value}
            </p>
          ))}
          <p>Flagged to customer {feedback.flag_day}</p>
          <p>
            Feedback received{' '}
            {formatDistanceToNow(new Date(feedback.uploaded_at), {addSuffix: true})}, uploaded by{' '}
            {feedback.uploaded_by}
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// The grid's "Status Check" column - the latest platform re-check. A submission that has never
// been checked shows an em dash rather than "unknown": unknown is itself a check result (the
// platform answered and the answer was unreadable), and conflating the two hides which rows the
// runner hasn't reached.
export function PlatformStatusCell({sub}: {readonly sub: StatusCheckFields}) {
  // The platform's own explanation for a removal is what analysts check by hand, so it sits under
  // the status rather than behind a hover. A message left over from before a restore is not shown.
  const reason = sub.platform_status === 'removed' ? sub.platform_feedback_message : null;
  const reasonLabel = sub.platform_reason_code
    ? PLATFORM_REASON_LABELS[sub.platform_reason_code]
    : undefined;
  const restrictions =
    sub.platform_status === 'restricted'
      ? (sub.platform_changes ?? []).flatMap((key) => PLATFORM_CHANGES[key]?.short ?? [])
      : [];

  return (
    <span className="flex flex-col items-start gap-0.5">
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <PlatformCheckStatus sub={sub} />
        <CustomerFeedbackIndicator sub={sub} />
      </span>
      {reason && reasonLabel ? (
        // The short label reads at a glance; YouTube's own sentence stays one hover away.
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="text-muted-foreground text-xs whitespace-nowrap">{reasonLabel}</span>
            }
          />
          <TooltipContent side="bottom" align="start" className="max-w-xs text-left">
            {reason}
          </TooltipContent>
        </Tooltip>
      ) : reason ? (
        // Narrow enough that the status and its change icons set the column width, not this line.
        <TruncatedText className="text-muted-foreground max-w-36 text-xs">{reason}</TruncatedText>
      ) : restrictions.length > 0 ? (
        <span className="text-muted-foreground text-xs">{restrictions.join(' · ')}</span>
      ) : null}
    </span>
  );
}

// Short labels for PlatformFeedbackMessageCatalog's reason codes (src/PRI.Shared/Submissions). A code
// missing here falls back to the truncated raw message, so a new server rule never shows blank.
const PLATFORM_REASON_LABELS: Record<string, string> = {
  RemovedByUploader: 'Uploader removed',
  RemovedNuditySexualContent: 'Nudity / sexual',
  RemovedViolentGraphicContent: 'Violent / graphic',
  RemovedHateSpeech: 'Hate speech',
  RemovedHarassmentBullying: 'Harassment / bullying',
  RemovedCommunityGuidelines: 'Community Guidelines',
  RemovedCopyright: 'Copyright',
  RemovedPrivacyClaim: 'Privacy claim',
  AccountTerminated: 'Account terminated',
  RemovedTermsOfService: 'Terms of Service',
  Private: 'Private',
  GeoBlocked: 'Geo-blocked',
  AgeRestrictedGate: 'Age-gated',
  RemovedUnspecified: 'No reason given'
};

function PlatformCheckStatus({sub}: {readonly sub: StatusCheckFields}) {
  if (!sub.platform_status && !sub.last_status_check_at) {
    return <span className="text-muted-foreground">—</span>;
  }

  const isRemoved = sub.platform_status === 'removed';
  const changes = (sub.platform_changes ?? []).flatMap((key) => {
    const change = PLATFORM_CHANGES[key];
    return change ? [{key, ...change}] : [];
  });
  const body = (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      <StatusIcon tone={platformStatusTone(sub.platform_status)} />
      <span className={cn('font-medium capitalize', isRemoved && 'text-destructive-ink')}>
        {sub.platform_status ?? 'Unknown'}
      </span>
      {/* A warning Badge rather than warning-inked glyphs: --warning as ink on a card is about
          2.4:1, under the 3:1 a graphic needs, where the filled badge carries its own foreground. */}
      {changes.map(({key, label, icon: Icon}) => (
        <Badge key={key} {...markerBadge('warning')}>
          <Icon aria-label={label} role="img" />
        </Badge>
      ))}
    </span>
  );

  if (!sub.last_status_check_at) {
    return body;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={body} />
      <TooltipContent side="bottom" align="start" className="max-w-xs text-left">
        <div className="space-y-1">
          <p className="font-medium">
            Checked {formatDistanceToNow(new Date(sub.last_status_check_at), {addSuffix: true})}
          </p>
          {changes.length > 0 ? (
            <ul className="list-disc space-y-0.5 pl-4">
              {changes.map(({key, label}) => (
                <li key={key}>{label}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function RemoveSubmissionDialog({
  open,
  submitting,
  error,
  onCancel,
  onConfirm
}: {
  readonly open: boolean;
  readonly submitting: boolean;
  readonly error?: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const reasonId = useId();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setReason('');
        } else {
          onCancel();
        }
      }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove submission</DialogTitle>
          <DialogDescription>
            It can be re-added later by reclassifying the same content. Please explain why
            you&rsquo;re removing it.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
          <Textarea
            autoFocus
            id={reasonId}
            rows={3}
            placeholder="Reason for removal…"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={submitting} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={submitting || reason.trim().length === 0}
            onClick={() => {
              onConfirm(reason.trim());
            }}>
            {submitting ? (
              <>
                <Spinner />
                Removing…
              </>
            ) : (
              'Remove'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type SubmissionsTableProps = {
  readonly submissions: readonly Submission[];
  readonly selected: ReadonlySet<string>;
  readonly openSubmissionId: string | null;
  readonly onToggleRow: (id: string) => void;
  // Selects every loaded, selectable row - or clears the selection when they already all are.
  readonly onToggleAll: () => void;
  readonly onOpenDetail: (sub: Submission) => void;
  // Flags the rows' items - flags live on items only, so every row of the same item follows.
  readonly onSetFlag: (rows: Submission[], color: FlagColor | null) => void;
  readonly onRetried: (id: string) => void;
  readonly onRequestRemove: (sub: Submission) => void;
  readonly onResubmit: (sub: Submission) => void;
  // Rendered as a full-width last row, inside the table's scroll region.
  readonly footer?: ReactNode;
};

// Checkbox, flag, 9 data columns and the actions menu - keep in step with the header row below.
const COLUMN_COUNT = 12;

export function SubmissionsTable({
  submissions,
  selected,
  openSubmissionId,
  onToggleRow,
  onToggleAll,
  onOpenDetail,
  onSetFlag,
  onRetried,
  onRequestRemove,
  onResubmit,
  footer
}: SubmissionsTableProps) {
  // "All" means every row loaded - the infinite scroll's later pages aren't included until they've
  // been scrolled into view. Historic rows are selectable too; the bulk bar offers only what applies
  // to them.
  const selectedCount = submissions.filter((s) => selected.has(s.id)).length;
  const allSelected = submissions.length > 0 && selectedCount === submissions.length;

  // One lightbox for the whole table, opened on a row's thumbnail; the row only reports the click.
  const [lightbox, setLightbox] = useState<{
    sub: Submission;
    revealed: boolean;
    colourised: boolean;
  } | null>(null);
  const openLightbox = useCallback((sub: Submission, revealed: boolean, colourised: boolean) => {
    setLightbox({sub, revealed, colourised});
  }, []);

  // Only the header cells stick, not the row, so the row's bottom border scrolls away with the body
  // and the divider ends up on whichever row slides under. An inset shadow on the cells travels
  // with them instead.
  return (
    <Table containerClassName="min-h-0 overflow-y-auto [&_thead_th]:bg-card [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:shadow-[inset_0_-1px_0_var(--border)]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-6">
            <Checkbox
              aria-label={allSelected ? 'Clear selection' : 'Select all loaded submissions'}
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              disabled={submissions.length === 0}
              onCheckedChange={() => {
                onToggleAll();
              }}
            />
          </TableHead>
          <TableHead className="w-6">
            <span className="sr-only">Flag</span>
          </TableHead>
          <TableHead>Content ID</TableHead>
          <TableHead>Delivery Status</TableHead>
          <TableHead>Status Check</TableHead>
          <TableHead>Content</TableHead>
          <TableHead>Policy / Risks</TableHead>
          <TableHead>Language / Location</TableHead>
          <TableHead>Notes</TableHead>
          <TableHead>Analyst</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="w-6">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {submissions.map((sub) => (
          <SubmissionRow
            key={sub.id}
            sub={sub}
            selected={selected.has(sub.id)}
            isOpen={openSubmissionId === sub.id}
            onToggle={() => {
              onToggleRow(sub.id);
            }}
            onOpenDetail={() => {
              onOpenDetail(sub);
            }}
            onSetFlag={(color) => {
              onSetFlag([sub], color);
            }}
            onRetried={onRetried}
            onRequestRemove={onRequestRemove}
            onResubmit={onResubmit}
            onOpenThumbnail={openLightbox}
          />
        ))}
        {footer ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={COLUMN_COUNT} className="p-0">
              {footer}
            </TableCell>
          </TableRow>
        ) : null}
        {/* Portalled, so it adds no DOM inside the body; it lives here only to share the table's
            lifetime. */}
        <ImageLightbox
          open={lightbox !== null}
          revealed={lightbox?.revealed ?? false}
          colourised={lightbox?.colourised ?? false}
          imageUrl={lightbox?.sub.thumbnail_url ?? null}
          alt={
            lightbox?.sub.title ??
            lightbox?.sub.display_name ??
            lightbox?.sub.handle ??
            lightbox?.sub.item_external_id ??
            lightbox?.sub.content_id ??
            ''
          }
          onClose={() => {
            setLightbox(null);
          }}
        />
      </TableBody>
    </Table>
  );
}

// A status Badge shrunk to hold one 12px glyph.
function markerBadge(tone: StatusTone, className?: string) {
  const {variant, className: toneClass} = toneBadge(tone);
  return {variant, className: cn('h-4 px-1', toneClass, className)};
}

// A per-row glyph whose label lives in its tooltip. One that is the only sign of a state takes a
// status Badge, not a status token as ink: --success and --info are surfaces, and as a 12px glyph
// on the row they sit under the 3:1 a graphic needs, where the filled Badge carries its own
// foreground. One that is only a hint stays muted ink, which is a text token.
export function RowMarker({
  icon: Icon,
  label,
  variant
}: {
  readonly icon: typeof Scale;
  readonly label: string;
  readonly variant?: StatusTone;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          variant ? (
            <Badge {...markerBadge(variant, 'ml-1.5')}>
              <Icon role="img" aria-label={label} />
            </Badge>
          ) : (
            <Icon
              role="img"
              aria-label={label}
              className="text-muted-foreground ml-1.5 inline size-3 shrink-0"
            />
          )
        }
      />
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

function SubmissionRow({
  sub,
  selected,
  isOpen,
  onToggle,
  onOpenDetail,
  onSetFlag,
  onRetried,
  onRequestRemove,
  onResubmit,
  onOpenThumbnail
}: {
  readonly sub: Submission;
  readonly selected: boolean;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly onOpenDetail: () => void;
  readonly onSetFlag: (color: FlagColor | null) => void;
  readonly onRetried: (id: string) => void;
  readonly onRequestRemove: (sub: Submission) => void;
  readonly onResubmit: (sub: Submission) => void;
  readonly onOpenThumbnail: (sub: Submission, revealed: boolean, colourised: boolean) => void;
}) {
  // The display_name is stamped from the item's title when it has one, so it would repeat the line above.
  const displayName =
    sub.author_name ??
    (sub.display_name === sub.title ? null : sub.display_name) ??
    sub.handle ??
    null;
  const analyst = sub.created_by.split('@')[0] ?? sub.created_by;
  const created = new Date(sub.created_at);
  const thumbnailClass = THUMBNAIL_SIZE_CLASS[useUiStore((s) => s.submissionsThumbnailSize)];
  const {data: apolloConfig} = useApolloRelayConfig();
  const apolloSending = apolloConfig?.sending ?? false;
  const queryClient = useQueryClient();

  const [retrying, setRetrying] = useState(false);
  const [apolloInFlight, setApolloInFlight] = useState(false);

  async function handleRetry() {
    setRetrying(true);
    try {
      await submissionsApi.retry(sub.id);
      onRetried(sub.id);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Retry failed'), 'error');
    } finally {
      setRetrying(false);
    }
  }

  async function handleSendToApollo() {
    setApolloInFlight(true);
    try {
      await submissionsApi.sendToApollo(sub.id);
      showToast('Queued for Apollo');
      // The drawer's history is a separate query nothing else invalidates - without this the new
      // timeline entry only appears after the drawer is closed and reopened.
      await queryClient.invalidateQueries({queryKey: ['submissions', sub.id, 'history']});
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Send to Apollo failed'), 'error');
    } finally {
      setApolloInFlight(false);
    }
  }

  // "not sent - removed" is a delivery that never happened, so it retries like a failed one. It is
  // also a terminal lifecycle state (SubmissionAggregate.Skipped) from which neither SubmissionRemoved
  // nor SubmissionApolloSendRequested is a legal transition - offering either would only ever 422.
  const canRetry =
    (sub.action_status === 'delivery failed' || sub.action_status === 'not sent - removed') &&
    !sub.is_historic;
  // Without an item id there is nothing to resubmit, so the action is left out rather than disabled.
  const canResubmit = !sub.is_historic && Boolean(sub.item_id);
  const canApollo = apolloSending && !sub.is_historic && sub.action_status !== 'not sent - removed';
  const canRemove =
    sub.action_status !== 'sent to client' &&
    sub.action_status !== 'not sent - removed' &&
    !sub.is_historic;
  const hasActions = canRetry || canResubmit || canApollo || canRemove;
  const actionInFlight = retrying || apolloInFlight;
  const contentId = sub.item_external_id ?? sub.content_id;

  return (
    <TableRow
      data-state={selected ? 'selected' : undefined}
      className={cn('cursor-pointer', isOpen && !selected && 'bg-muted/50')}
      onClick={onOpenDetail}>
      <TableCell
        onClick={(event) => {
          event.stopPropagation();
        }}>
        <Checkbox
          aria-label={`Select ${contentId}`}
          checked={selected}
          onCheckedChange={() => {
            onToggle();
          }}
        />
      </TableCell>

      <TableCell
        className="px-1"
        onClick={(event) => {
          event.stopPropagation();
        }}>
        {/* A historic row, or one with no item, has no item to hang a flag off. */}
        {sub.is_historic || !sub.item_id ? null : (
          <FlagPicker value={sub.flag} onChange={onSetFlag} />
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap">
        <span className="font-mono text-xs font-medium">{contentId}</span>
        {sub.source_url ? (
          <CopyUrlButton url={sub.source_url} className="ml-0.5 align-middle" />
        ) : null}
        {sub.resubmission_ordinal > 1 ? (
          <Badge variant="outline" className="ml-1.5 h-4 px-1 font-mono">
            #{sub.resubmission_ordinal}
          </Badge>
        ) : null}
        {sub.has_calibration ? (
          <RowMarker icon={Scale} label="Calibrated" variant="success" />
        ) : null}
        {sub.has_calibration_notes && !sub.has_calibration ? (
          <RowMarker icon={NotebookPen} label="Has calibration notes" />
        ) : null}
        {sub.has_reviewer_comments ? (
          <RowMarker icon={MessageSquare} label="Has reviewer comments" variant="info" />
        ) : null}
        <div className="mt-1">
          <Badge variant="secondary">{sub.submission_type}</Badge>
        </div>
      </TableCell>

      <TableCell>
        <SubmissionStatusPill status={sub.action_status} />
      </TableCell>

      <TableCell>
        <PlatformStatusCell sub={sub} />
      </TableCell>

      <TableCell className="max-w-[380px]">
        <div className="flex items-center gap-3">
          {/* Propagation stops so toggling the blur pill doesn't also open the row's detail. */}
          <div
            role="presentation"
            className="shrink-0"
            onClick={(event) => {
              event.stopPropagation();
            }}>
            {sub.thumbnail_url ? (
              <SensitiveMediaButton
                compact
                label={`Expand thumbnail for ${displayName ?? contentId}`}
                src={sub.thumbnail_url}
                alt=""
                className={cn('bg-muted overflow-hidden', thumbnailClass)}
                imageClassName="h-full w-full object-cover"
                onClick={(blurred, greyed) => {
                  onOpenThumbnail(sub, !blurred, !greyed);
                }}
              />
            ) : (
              <div
                className={cn(
                  'bg-muted flex items-center justify-center overflow-hidden',
                  thumbnailClass
                )}>
                <Film className="text-muted-foreground size-4" />
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <TruncatedText>{sub.title ?? '—'}</TruncatedText>
            <div className="flex min-w-0">
              {/* The one focusable way into the detail: the row's own onClick is pointer-only, so a
                  keyboard user had nothing to land on. Propagation stops here or the row would open
                  the same detail a second time. */}
              <Button
                variant="link"
                size="xs"
                aria-label={`Open ${displayName ?? contentId}`}
                // Button is shrink-0 by default, which pushed it out of the cell instead of truncating.
                className="h-auto min-w-0 shrink justify-start p-0"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenDetail();
                }}>
                <TruncatedText className="min-w-0">
                  {displayName ?? '—'}
                  {sub.handle && sub.handle !== displayName ? (
                    <span className="text-muted-foreground">
                      {' '}
                      {sub.handle.startsWith('@') ? sub.handle : `@${sub.handle}`}
                    </span>
                  ) : null}
                </TruncatedText>
              </Button>
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className="max-w-[200px]">
        <div className="flex flex-col items-start gap-1">
          <Badge variant="outline">{sub.policy_violation}</Badge>
          <TruncatedText className="text-muted-foreground max-w-full text-xs">
            {sub.risks.join(', ') || '—'}
          </TruncatedText>
        </div>
      </TableCell>

      <TableCell className="text-muted-foreground max-w-[160px]">
        <TruncatedText>{sub.language.join(', ') || '—'}</TruncatedText>
        <TruncatedText className="text-xs">{sub.location.join(', ') || '—'}</TruncatedText>
      </TableCell>

      <TableCell className="max-w-[280px]">
        {sub.notes || sub.has_attachments ? (
          <span className="flex items-center gap-1.5">
            {sub.has_attachments ? (
              <Images
                className="text-muted-foreground size-3.5 shrink-0"
                aria-label="Has screenshots"
              />
            ) : null}
            <TruncatedText className="min-w-0">{sub.notes}</TruncatedText>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>

      <TableCell className="text-muted-foreground">{analyst}</TableCell>

      <TableCell
        className="text-muted-foreground whitespace-nowrap"
        title={format(created, 'PPpp')}>
        <div>{formatDistanceToNow(created, {addSuffix: true})}</div>
        <div className="text-xs">{format(created, 'd MMM yyyy HH:mm')}</div>
      </TableCell>

      <TableCell
        onClick={(event) => {
          event.stopPropagation();
        }}>
        <div className="flex items-center justify-end">
          {hasActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="Submission actions"
                disabled={actionInFlight}
                render={
                  <Button variant="ghost" size="icon-sm" className="text-muted-foreground" />
                }>
                {actionInFlight ? <Spinner /> : <MoreHorizontal />}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canApollo ? (
                  <DropdownMenuItem onClick={() => void handleSendToApollo()}>
                    <SendHorizontal />
                    Send to Apollo
                  </DropdownMenuItem>
                ) : null}
                {canRetry ? (
                  <DropdownMenuItem onClick={() => void handleRetry()}>
                    <RotateCw />
                    Retry
                  </DropdownMenuItem>
                ) : null}
                {canResubmit ? (
                  <DropdownMenuItem
                    onClick={() => {
                      onResubmit(sub);
                    }}>
                    <RefreshCw />
                    Resubmit
                  </DropdownMenuItem>
                ) : null}
                {canRemove ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        onRequestRemove(sub);
                      }}>
                      <Trash2 />
                      Remove
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}
